/* ==========================================================================
   Integrated Management System (IMS) Policy Page Component
   Authentic Multilingual Implementation for JALUD Embedded s.r.o.
   Covers ISO 9001:2015 and ISO/IEC 27001:2022
   Languages: CS, DE, EN, ES, PT, AR (with strict RTL support)
   ========================================================================== */

import { getLocale } from '../locales/i18n.js';

const imsData = {
  cs: {
    eyebrow: 'KVALITA A BEZPEČNOST INFORMACÍ',
    title: 'Politika integrovaného systému řízení (IMS)',
    subtitle: 'Zahrnuje požadavky norem ISO 9001:2015 a ISO/IEC 27001:2022.',
    dateStr: 'Plzeň, 6. února 2025',
    pdfBtn: 'Zobrazit anglický originál PDF →',
    pdfUrl: '/docs/legal/EN_SM_26_Politika_ISMS.pdf',
    sections: [
      {
        num: '1',
        title: 'QMS – Systém managementu kvality',
        text: 'Jsme plně oddáni porozumění a naplňování potřeb a požadavků našich zákazníků a zajištění nejvyšší kvality. Neustále zlepšujeme naše procesy, produkty a služby, abychom zajistili spolehlivost, bezpečnost a soulad s nejvyššími standardy. Naším cílem je dosáhnout nulového výskytu neshod v procesech, produktech a službách prostřednictvím systematického zavádění preventivních opatření a podpory kultury neustálého zlepšování.'
      },
      {
        num: '2',
        title: 'ISMS – Systém řízení bezpečnosti informací',
        text: 'Jako společnost nakládající s citlivými informacemi a osobními údaji klademe na jejich ochranu nejvyšší prioritu. Identifikujeme a řídíme rizika bezpečnosti informací, dodržujeme zákonné požadavky včetně GDPR a směrnice NIS2 a pravidelně školíme zaměstnance v oblasti bezpečnosti a ochrany informací. Přístup k datům se řídí přísným principem „need-to-know“ a zásadou nejnižších nutných oprávnění. Naším cílem je dosáhnout nulového počtu bezpečnostních incidentů prostřednictvím účinné prevence, nepřetržitého monitorování a včasné reakce na potenciální hrozby.'
      },
      {
        num: '3',
        title: 'Společenská odpovědnost a udržitelnost',
        text: 'Uvědomujeme si svůj vliv na společnost a životní prostředí, a proto jednáme odpovědně a udržitelně ve všech aspektech našeho podnikání. Aktivně podporujeme iniciativy, které přispívají k ochraně života, majetku a zdraví, a současně minimalizujeme náš dopad na životní prostředí prostřednictvím opatření, jako je optimalizace spotřeby energie a zdrojů.'
      },
      {
        num: '4',
        title: 'Řízení rizik',
        text: 'Pravidelně provádíme analýzu rizik v rámci systémů ISMS i QMS a zavádíme opatření ke zmírnění identifikovaných hrozeb. Naším cílem je minimalizovat dopady rizik a snižovat pravděpodobnost vzniku incidentů.'
      },
      {
        num: '5',
        title: 'BCP – Plánování kontinuity provozu',
        text: 'Máme vytvořeny plány kontinuity činností a havarijní obnovy pro případ selhání systémů nebo kybernetických incidentů. Tyto plány jsou pravidelně testovány, aby byl zajištěn nepřetržitý provoz i během mimořádných událostí a minimalizován dopad krizových situací.'
      },
      {
        num: '6',
        title: 'Neustálé zlepšování',
        text: 'Pravidelně auditujeme a přezkoumáváme systémy ISMS a QMS, abychom zvyšovali jejich účinnost. Tím zajišťujeme soulad s normami ISO 9001 a ISO 27001 a neustále zvyšujeme úroveň řízení kvality a bezpečnosti.'
      },
      {
        num: '7',
        title: 'Zákonné požadavky',
        text: 'Důsledně dodržujeme platné právní předpisy a standardy v oblasti kvality a kybernetické bezpečnosti, čímž předcházíme právním a finančním důsledkům jejich případného neplnění.'
      },
      {
        num: '8',
        title: 'Rozvoj povědomí',
        text: 'Pravidelně provádíme školení v oblasti bezpečnosti informací a řízení kvality, abychom zvyšovali povědomí zaměstnanců o bezpečnostních a kvalitativních požadavcích, a tím omezili vliv lidského faktoru na vznik bezpečnostních a provozních incidentů.'
      },
      {
        num: '9',
        title: 'Závazek vedení společnosti',
        text: 'Vedení společnosti JALUD Embedded s.r.o. se zavazuje k aktivní podpoře ISMS a vyčlenění nezbytných zdrojů na jeho udržování. Naším cílem je zajistit odpovědné řízení bezpečnosti informací, snižovat bezpečnostní rizika a prosazovat kulturu bezpečnosti a kvality v celé společnosti.'
      }
    ]
  },
  de: {
    eyebrow: 'QUALITÄT UND INFORMATIONSSICHERHEIT',
    title: 'Politik des integrierten Managementsystems (IMS)',
    subtitle: 'Umfasst die Anforderungen der ISO 9001:2015 und der ISO/IEC 27001:2022.',
    dateStr: 'Plzeň, 6. Februar 2025',
    pdfBtn: 'Originaldokument als PDF anzeigen →',
    pdfUrl: '/docs/legal/Integrierte_Managementsystem_Politik_IMS.pdf',
    sections: [
      {
        num: '1',
        title: 'QMS – Qualitätsmanagementsystem',
        text: 'Wir verpflichten uns vollständig, die Bedürfnisse und Anforderungen unserer Kunden zu verstehen und zu erfüllen, um höchste Qualität sicherzustellen. Wir verbessern kontinuierlich unsere Prozesse, Produkte und Dienstleistungen, um Zuverlässigkeit, Sicherheit und die Einhaltung der höchsten Standards zu gewährleisten. Unser Ziel ist es, durch konsequente Umsetzung präventiver Maßnahmen und die Förderung einer Kultur der kontinuierlichen Verbesserung Null Prozess-, Produkt- und Dienstleistungsabweichungen zu erreichen.'
      },
      {
        num: '2',
        title: 'ISMS – Informationssicherheits-Managementsystem',
        text: 'Als Unternehmen, das sensible Informationen und personenbezogene Daten verarbeitet, hat der Schutz dieser Daten für uns höchste Priorität. Wir identifizieren und steuern Risiken in der Informationssicherheit, erfüllen gesetzliche Anforderungen einschließlich DSGVO und NIS2 und schulen unsere Mitarbeitenden regelmäßig in Sicherheit und Informationsschutz. Der Datenzugang folgt streng dem Need-to-know-Prinzip sowie dem Prinzip der geringsten Rechte. Unser Ziel ist es, durch wirksame Prävention, kontinuierliche Überwachung und rechtzeitige Reaktion auf potenzielle Bedrohungen Null Sicherheitsvorfälle zu erreichen.'
      },
      {
        num: '3',
        title: 'Soziale Verantwortung und Nachhaltigkeit',
        text: 'Wir sind uns unserer Auswirkungen auf Gesellschaft und Umwelt bewusst und handeln daher in allen Bereichen unseres Geschäfts verantwortungsbewusst und nachhaltig. Wir unterstützen aktiv Initiativen, die zum Schutz von Leben, Eigentum und Gesundheit beitragen, und minimieren gleichzeitig unsere Umweltbelastung, unter anderem durch Optimierung des Energie- und Ressourcenverbrauchs.'
      },
      {
        num: '4',
        title: 'Risikomanagement',
        text: 'Wir führen regelmäßige Risikoanalysen sowohl im ISMS als auch im QMS durch und setzen Maßnahmen zur Minderung identifizierter Risiken um. Unser Ziel ist es, die Auswirkungen potenzieller Risiken zu minimieren und die Wahrscheinlichkeit von Vorfällen zu reduzieren.'
      },
      {
        num: '5',
        title: 'BCP – Business Continuity Planning',
        text: 'Wir verfügen über Pläne zur Geschäftskontinuität und Wiederherstellung (Disaster Recovery) für den Fall von Systemausfällen oder Cybervorfällen. Diese Pläne werden regelmäßig getestet, um einen unterbrechungsfreien Betrieb bei Störungen sicherzustellen und die Auswirkungen von Krisensituationen zu minimieren.'
      },
      {
        num: '6',
        title: 'Kontinuierliche Verbesserung',
        text: 'Wir auditieren und überprüfen ISMS und QMS regelmäßig, um deren Wirksamkeit zu steigern. Dadurch gewährleisten wir die Einhaltung von ISO 9001 und ISO 27001 und verbessern kontinuierlich das Niveau unseres Qualitäts- und Sicherheitsmanagements.'
      },
      {
        num: '7',
        title: 'Gesetzliche Anforderungen',
        text: 'Wir halten alle relevanten gesetzlichen Vorgaben und Normen im Bereich Qualität und Cybersicherheit ein, um rechtliche und finanzielle Folgen bei Nichteinhaltung zu vermeiden.'
      },
      {
        num: '8',
        title: 'Bewusstseinsentwicklung',
        text: 'Wir führen regelmäßig Schulungen zur Informationssicherheit und zum Qualitätsmanagement durch, um das Bewusstsein der Mitarbeitenden für Sicherheits- und Qualitätsanforderungen zu stärken. Dadurch wird das Risiko menschlicher Fehler als Ursache von Sicherheits- und Qualitätsvorfällen reduziert.'
      },
      {
        num: '9',
        title: 'Engagement der Unternehmensleitung',
        text: 'Die Geschäftsleitung von JALUD Embedded s.r.o. verpflichtet sich, das ISMS aktiv zu unterstützen und die notwendigen Ressourcen für dessen Aufrechterhaltung bereitzustellen. Unser Ziel ist es, ein verantwortungsbewusstes Informationssicherheitsmanagement zu gewährleisten, Sicherheitsrisiken zu reduzieren und eine Kultur der Sicherheit und Qualität im Unternehmen zu fördern.'
      }
    ]
  },
  en: {
    eyebrow: 'QUALITY AND INFORMATION SECURITY',
    title: 'Integrated Management System Policy (IMS)',
    subtitle: 'Includes the requirements of ISO 9001:2015 and ISO/IEC 27001:2022.',
    dateStr: 'Plzeň, 6 February 2025',
    pdfBtn: 'View the original PDF →',
    pdfUrl: '/docs/legal/EN_SM_26_Politika_ISMS.pdf',
    sections: [
      {
        num: '1',
        title: 'QMS – Quality Management System',
        text: 'We are fully committed to understanding and meeting our customers\' needs and requirements, ensuring the highest quality. We continuously improve our processes, products, and services to ensure reliability, security, and compliance with the highest standards. Our goal is to achieve zero process, product, and service nonconformities through systematic implementation of preventive measures and fostering a culture of continuous improvement.'
      },
      {
        num: '2',
        title: 'ISMS – Information Security Management System',
        text: 'As a company handling sensitive information and personal data, we prioritize their protection. We identify and manage information security risks, comply with legal requirements, including GDPR and NIS2, and regularly train employees in security and information protection. Data access follows the “need-to-know” principle and the principle of least privilege. Our goal is to achieve zero security incidents through effective prevention, continuous monitoring, and timely response to potential threats.'
      },
      {
        num: '3',
        title: 'Social Responsibility and Sustainability',
        text: 'We recognize our impact on society and the environment and therefore act responsibly and sustainably in all aspects of our business. We actively support initiatives that contribute to the protection of life, property, and health while minimizing our environmental impact through measures such as optimizing energy and resource consumption.'
      },
      {
        num: '4',
        title: 'Risk Management',
        text: 'We regularly conduct risk analysis in both ISMS and QMS, implementing measures to mitigate identified threats. Our objective is to minimize risk impact and reduce the likelihood of incidents.'
      },
      {
        num: '5',
        title: 'BCP – Business Continuity Planning',
        text: 'We have established business continuity and disaster recovery plans for system failures or cyber incidents. These plans are regularly tested to ensure uninterrupted operations during disruptions and to minimize the impact of crisis situations.'
      },
      {
        num: '6',
        title: 'Continuous Improvement',
        text: 'We regularly audit and review ISMS to enhance its effectiveness. This ensures compliance with ISO 9001 and ISO 27001, continuously improving the quality and security management levels.'
      },
      {
        num: '7',
        title: 'Legislation',
        text: 'We adhere to applicable laws and standards in quality and cybersecurity, preventing legal and financial consequences of non-compliance.'
      },
      {
        num: '8',
        title: 'Awareness Development',
        text: 'We regularly conduct training in information security and quality management to enhance employees’ awareness of security and quality requirements, thereby reducing human error as a factor in security and quality incidents.'
      },
      {
        num: '9',
        title: 'Management Commitment',
        text: 'The leadership of JALUD Embedded s.r.o. is committed to supporting ISMS and allocating the necessary resources to maintain it. Our goal is to ensure responsible information security management, reduce security risks, and promote a culture of security and quality within the company.'
      }
    ]
  },
  es: {
    eyebrow: 'CALIDAD Y SEGURIDAD DE LA INFORMACIÓN',
    title: 'Política del Sistema Integrado de Gestión (IMS)',
    subtitle: 'Incluye los requisitos de las normas ISO 9001:2015 e ISO/IEC 27001:2022.',
    dateStr: 'Plzeň, 6 de febrero de 2025',
    pdfBtn: 'Ver el documento original en inglés (PDF) →',
    pdfUrl: '/docs/legal/EN_SM_26_Politika_ISMS.pdf',
    sections: [
      {
        num: '1',
        title: 'QMS – Sistema de Gestión de la Calidad',
        text: 'Estamos plenamente comprometidos a comprender y satisfacer las necesidades y requisitos de nuestros clientes, garantizando la más alta calidad. Mejoramos continuamente nuestros procesos, productos y servicios para asegurar la fiabilidad, la seguridad y el cumplimiento de los estándares más exigentes. Nuestro objetivo es lograr cero no conformidades en procesos, productos y servicios mediante la aplicación sistemática de medidas preventivas y el fomento de una cultura de mejora continua.'
      },
      {
        num: '2',
        title: 'ISMS – Sistema de Gestión de Seguridad de la Información',
        text: 'Como empresa que maneja información confidencial y datos personales, priorizamos su estricta protección. Identificamos y gestionamos los riesgos de seguridad de la información, cumplimos los requisitos legales, incluidos el RGPD y la directiva NIS2, y capacitamos periódicamente a nuestros empleados en seguridad y protección de la información. El acceso a los datos sigue el principio de «necesidad de conocer» y el principio de mínimo privilegio. Nuestro objetivo es alcanzar cero incidentes de seguridad mediante la prevención eficaz, la supervisión continua y la respuesta oportuna ante posibles amenazas.'
      },
      {
        num: '3',
        title: 'Responsabilidad social y sostenibilidad',
        text: 'Reconocemos nuestro impacto en la sociedad y el medio ambiente y, por tanto, actuamos de manera responsable y sostenible en todos los aspectos de nuestro negocio. Apoyamos activamente iniciativas que contribuyen a la protección de la vida, la propiedad y la salud, minimizando al mismo tiempo nuestro impacto ambiental mediante medidas como la optimización del consumo de energía y recursos.'
      },
      {
        num: '4',
        title: 'Gestión de riesgos',
        text: 'Realizamos periódicamente análisis de riesgos tanto en el ISMS como en el QMS, aplicando medidas para mitigar las amenazas identificadas. Nuestro objetivo es minimizar el impacto de los riesgos y reducir la probabilidad de incidentes.'
      },
      {
        num: '5',
        title: 'BCP – Planificación de la continuidad del negocio',
        text: 'Hemos establecido planes de continuidad del negocio y de recuperación ante desastres para fallos del sistema o incidentes cibernéticos. Estos planes se prueban con regularidad para garantizar un funcionamiento ininterrumpido durante las incidencias y minimizar el impacto de situaciones de crisis.'
      },
      {
        num: '6',
        title: 'Mejora continua',
        text: 'Auditamos y revisamos periódicamente el ISMS para mejorar su eficacia. Esto garantiza el cumplimiento de las normas ISO 9001 e ISO 27001, perfeccionando continuamente los niveles de gestión de calidad y seguridad.'
      },
      {
        num: '7',
        title: 'Legislación',
        text: 'Cumplimos rigurosamente con las leyes y estándares aplicables en materia de calidad y ciberseguridad, previniendo las consecuencias legales y financieras del incumplimiento.'
      },
      {
        num: '8',
        title: 'Desarrollo de la concienciación',
        text: 'Llevamos a cabo periódicamente formaciones sobre seguridad de la información y gestión de la calidad para aumentar la concienciación de los empleados, reduciendo así el error humano como factor en incidentes de seguridad y calidad.'
      },
      {
        num: '9',
        title: 'Compromiso de la dirección',
        text: 'La dirección de JALUD Embedded s.r.o. se compromete a respaldar el ISMS y a asignar los recursos necesarios para su mantenimiento. Nuestro objetivo es garantizar una gestión responsable de la seguridad de la información, reducir los riesgos y promover una sólida cultura de seguridad y calidad en toda la empresa.'
      }
    ]
  },
  pt: {
    eyebrow: 'QUALIDADE E SEGURANÇA DA INFORMAÇÃO',
    title: 'Política do Sistema Integrado de Gestão (IMS)',
    subtitle: 'Inclui os requisitos das normas ISO 9001:2015 e ISO/IEC 27001:2022.',
    dateStr: 'Plzeň, 6 de fevereiro de 2025',
    pdfBtn: 'Ver o documento original em inglês (PDF) →',
    pdfUrl: '/docs/legal/EN_SM_26_Politika_ISMS.pdf',
    sections: [
      {
        num: '1',
        title: 'QMS – Sistema de Gestão da Qualidade',
        text: 'Estamos totalmente empenhados em compreender e atender às necessidades e requisitos de nossos clientes, garantindo a mais alta qualidade. Melhoramos continuamente nossos processos, produtos e serviços para assegurar confiabilidade, segurança e conformidade com os mais altos padrões. Nossa meta é alcançar zero não conformidades em processos, produtos e serviços através da implementação sistemática de medidas preventivas e do fomento a uma cultura de melhoria contínua.'
      },
      {
        num: '2',
        title: 'ISMS – Sistema de Gestão da Segurança da Informação',
        text: 'Como empresa que lida com informações confidenciais e dados pessoais, priorizamos sua proteção rigorosa. Identificamos e gerenciamos riscos de segurança da informação, cumprimos os requisitos legais, incluindo GDPR e NIS2, e treinamos regularmente os colaboradores em segurança e proteção da informação. O acesso aos dados segue o princípio da «necessidade de saber» e o princípio do menor privilégio. Nossa meta é alcançar zero incidentes de segurança mediante prevenção eficaz, monitoramento contínuo e resposta oportuna a ameaças potenciais.'
      },
      {
        num: '3',
        title: 'Responsabilidade social e sustentabilidade',
        text: 'Reconhecemos nosso impacto na sociedade e no meio ambiente e, portanto, agimos de forma responsável e sustentável em todos os aspectos do nosso negócio. Apoiamos ativamente iniciativas que contribuem para a proteção da vida, do patrimônio e da saúde, enquanto minimizamos nosso impacto ambiental através de medidas como a otimização do consumo de energia e recursos.'
      },
      {
        num: '4',
        title: 'Gestão de riscos',
        text: 'Realizamos regularmente análises de riscos tanto no ISMS quanto no QMS, implementando medidas para mitigar ameaças identificadas. Nosso objetivo é minimizar o impacto dos riscos e reduzir a probabilidade de incidentes.'
      },
      {
        num: '5',
        title: 'BCP – Planejamento de Continuidade de Negócios',
        text: 'Estabelecemos planos de continuidade de negócios e recuperação de desastres para falhas de sistema ou incidentes cibernéticos. Esses planos são testados periodicamente para garantir operações ininterruptas durante perturbações e minimizar os impactos de situações de crise.'
      },
      {
        num: '6',
        title: 'Melhoria contínua',
        text: 'Auditamos e revisamos periodicamente o ISMS para aprimorar sua eficácia. Isso assegura a conformidade com as normas ISO 9001 e ISO 27001, elevando continuamente os níveis de gestão de qualidade e segurança.'
      },
      {
        num: '7',
        title: 'Legislação',
        text: 'Cumprimos rigorosamente as leis e normas aplicáveis em qualidade e cibersegurança, prevenindo consequências jurídicas e financeiras decorrentes do não cumprimento.'
      },
      {
        num: '8',
        title: 'Desenvolvimento da conscientização',
        text: 'Realizamos regularmente treinamentos em segurança da informação e gestão da qualidade para aumentar a conscientização dos funcionários quanto aos requisitos de segurança e qualidade, reduzindo o erro humano como fator de incidentes.'
      },
      {
        num: '9',
        title: 'Compromisso da liderança',
        text: 'A liderança da JALUD Embedded s.r.o. compromete-se a apoiar o ISMS e alocar os recursos necessários para a sua manutenção. Nosso objetivo é assegurar uma gestão responsável da segurança da informação, mitigar riscos e promover uma cultura de segurança e qualidade em toda a organização.'
      }
    ]
  },
  ar: {
    eyebrow: 'الجودة وأمن المعلومات',
    title: 'سياسة نظام الإدارة المتكامل (IMS)',
    subtitle: 'تشمل متطلبات المعيارين ISO 9001:2015 وISO/IEC 27001:2022.',
    dateStr: 'بلزن، 6 فبراير 2025',
    pdfBtn: 'عرض المستند الأصلي باللغة الإنجليزية (PDF) ←',
    pdfUrl: '/docs/legal/EN_SM_26_Politika_ISMS.pdf',
    sections: [
      {
        num: '1',
        title: 'QMS – نظام إدارة الجودة',
        text: 'نحن ملتزمون تماماً بفهم وتلبية احتياجات ومتطلبات عملائنا، وضمان أعلى مستويات الجودة. ونعمل باستمرار على تحسين عملياتنا ومنتجاتنا وخدماتنا لضمان الموثوقية والأمان والامتثال لأعلى المعايير. هدفنا هو تحقيق صفر حالات عدم مطابقة في العمليات والمنتجات والخدمات من خلال التنفيذ المنهجي للتدابير الوقائية وتعزيز ثقافة التحسين المستمر.'
      },
      {
        num: '2',
        title: 'ISMS – نظام إدارة أمن المعلومات',
        text: 'بصفتنا شركة تتعامل مع معلومات حساسة وبيانات شخصية، فإننا نولي حمايتها الأولوية القصوى. نقوم بتحديد وإدارة مخاطر أمن المعلومات، والامتثال للمتطلبات القانونية بما في ذلك GDPR وتوجيه NIS2، وتدريب الموظفين بانتظام على الأمان وحماية المعلومات. ويخضع الوصول إلى البيانات لمبدأ «الحاجة إلى المعرفة» ومبدأ الامتياز الأدنى. هدفنا هو تحقيق صفر حوادث أمنية من خلال الوقاية الفعالة والمراقبة المستمرة والاستجابة السريعة للتهديدات المحتملة.'
      },
      {
        num: '3',
        title: 'المسؤولية الاجتماعية والاستدامة',
        text: 'ندرك تماماً أثر أعمالنا على المجتمع والبيئة، ولذلك نتصرف بمسؤولية واستدامة في جميع جوانب أعمالنا. نحن ندعم بنشاط المبادرات التي تسهم في حماية الأرواح والممتلكات والصحة مع تقليل بصمتنا البيئية إلى أدنى حد من خلال تحسين استهلاك الطاقة والموارد.'
      },
      {
        num: '4',
        title: 'إدارة المخاطر',
        text: 'نجري بانتظام تحليلاً شاملاً للمخاطر ضمن نظامي ISMS و QMS، ونطبق تدابير ملموسة للتخفيف من التهديدات المحددة. هدفنا هو تقليل آثار المخاطر واحتمالية وقوع الحوادث.'
      },
      {
        num: '5',
        title: 'BCP – التخطيط لاستمرارية الأعمال',
        text: 'وضعنا خططاً محكمة لاستمرارية الأعمال والتعافي من الكوارث في حالات أعطال الأنظمة أو الحوادث السيبرانية. ويتم اختبار هذه الخطط بانتظام لضمان استمرار العمليات دون انقطاع أثناء الاضطرابات وتقليل تأثير الأزمات.'
      },
      {
        num: '6',
        title: 'التحسين المستمر',
        text: 'نقوم بالتدقيق والمراجعة المنتظمة لنظام ISMS لتعزيز كفاءته وفعاليته. وهذا يضمن الامتثال لمعايير ISO 9001 و ISO 27001، مما يرفع باستمرار من مستويات إدارة الجودة والأمان.'
      },
      {
        num: '7',
        title: 'المتطلبات التشريعية والقانونية',
        text: 'نلتزم بالكامل بجميع القوانين واللوائح والمعايير المعمول بها في مجالات الجودة والأمن السيبراني، مما يحول دون حدوث تبعات قانونية أو مالية نتيجة عدم الامتثال.'
      },
      {
        num: '8',
        title: 'تطوير الوعي الأمني',
        text: 'نعقد دورات تدريبية منتظمة في أمن المعلومات وإدارة الجودة لتعزيز وعي الموظفين بالمتطلبات الأمنية والنوعية، مما يحد من الأخطاء البشرية كعامل مسبب للحوادث الأمنية والتشغيلية.'
      },
      {
        num: '9',
        title: 'التزام الإدارة العليا',
        text: 'تلتزم إدارة شركة JALUD Embedded s.r.o. بدعم نظام ISMS وتخصيص الموارد اللازمة للحفاظ عليه وتطويره. هدفنا هو ضمان إدارة مسؤولة لأمن المعلومات، والحد من المخاطر الأمنية، وترسيخ ثقافة راسخة للأمان والجودة داخل الشركة بأكملها.'
      }
    ]
  }
};

export function renderPolicyIMSPage(onNavigate) {
  const container = document.createElement('div');
  container.className = 'sed-page';
  const lang = getLocale() || 'en';
  const data = imsData[lang] || imsData.en;
  const isRtl = lang === 'ar';

  container.innerHTML = `
    <section class="sed-section" style="background: linear-gradient(165deg, var(--blue-light) 0%, #ffffff 70%); padding: 4.5rem 0 3rem; direction: ${isRtl ? 'rtl' : 'ltr'}; text-align: ${isRtl ? 'right' : 'left'};">
      <div class="sed-container">
        <div class="sed-section-header" style="max-width: 860px; margin-bottom: 2rem; margin-left: ${isRtl ? 'auto' : '0'}; margin-right: ${isRtl ? '0' : 'auto'};">
          <div class="sed-eyebrow">${data.eyebrow}</div>
          <h1 class="sed-page-hero-title">${data.title}</h1>
          <p class="sed-section-desc">${data.subtitle}</p>
        </div>
      </div>
    </section>

    <section class="sed-section" style="background-color: #ffffff; padding: 2rem 0 5rem; direction: ${isRtl ? 'rtl' : 'ltr'}; text-align: ${isRtl ? 'right' : 'left'};">
      <div class="sed-container" style="max-width: 900px;">
        <div class="sed-card" style="padding: 2.75rem; line-height: 1.8; color: var(--text-dark); border: 1.5px solid var(--slate-line); border-radius: 16px; box-shadow: 0 4px 20px rgba(0,0,0,0.03);">
          
          <!-- Top Action Bar (Metadata + PDF Link) -->
          <div style="display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid var(--blue-light); padding-bottom: 1.25rem; margin-bottom: 2rem; flex-wrap: wrap; gap: 1rem;">
            <div>
              <span style="font-weight: 800; color: var(--navy-dark); font-size: 1.15rem; font-family: var(--font-heading);">
                JALUD Embedded s.r.o.
              </span><br>
              <span style="font-size: 0.88rem; color: var(--text-muted); font-weight: 600;">
                📅 ${data.dateStr}
              </span>
            </div>
            <a href="${data.pdfUrl}" target="_blank" rel="noopener noreferrer" class="sed-btn sed-btn-outline" style="font-size: 0.9rem; color: var(--blue-primary); border-color: var(--blue-primary); font-weight: 700; display: inline-flex; align-items: center; gap: 8px;">
              <span>📄</span>
              <span>${data.pdfBtn}</span>
            </a>
          </div>

          <!-- The 9 Official IMS Sections -->
          <div style="display: flex; flex-direction: column; gap: 2rem;">
            ${data.sections.map(sec => `
              <div style="border-bottom: 1px solid var(--slate-line); padding-bottom: 1.5rem;">
                <h3 style="font-family: var(--font-heading); font-size: 1.25rem; font-weight: 800; color: var(--navy-dark); margin-bottom: 0.65rem; display: flex; align-items: center; gap: 10px;">
                  <span style="background: var(--blue-ultra-light); color: var(--blue-primary); width: 30px; height: 30px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: 0.9rem; font-weight: 800; flex-shrink: 0;">
                    ${sec.num}
                  </span>
                  <span>${sec.title}</span>
                </h3>
                <p style="margin: 0; font-size: 1.02rem; line-height: 1.75; color: var(--text-dark);">
                  ${sec.text}
                </p>
              </div>
            `).join('')}
          </div>

        </div>
      </div>
    </section>
  `;

  return container;
}
